import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Page } from "@/services/bedrock/dtos/Page";

type FetchFn<T> = (search: string | undefined, page: number) => Promise<Page<T>>;

type SelectedValue<Value, Multiple extends boolean | undefined> = Multiple extends true
  ? Value[]
  : Value | null | undefined;
type OnValueChange<Value, Multiple extends boolean | undefined> = (
  value: SelectedValue<Value, Multiple>,
  eventDetails: any
) => void;

export interface PaginatedCombobox<T, Multiple extends boolean = false> {
  items: T[];
  value: SelectedValue<T, Multiple>;
  hasMore: boolean;
  isLoadingMore: boolean;
  multiple: Multiple;
  searchable: boolean;
  maxChips: number | "auto";
  allowAdditions: boolean;
  onAddItem: ((query: string) => T | undefined) | undefined;
  isItemEqualToValue: (itemValue: T, selectedValue: T) => boolean;
  onValueChange: OnValueChange<T, Multiple>;
  onLoadMore: () => void;
  onSearch: (query: string) => void;
  reset: () => void;
  /** Refetch from page 0 without clearing current items (avoids selection flicker). */
  reload: () => void;
}

export function usePaginatedCombobox<T extends { id: string | number }>(
  fetchFn: FetchFn<T>,
  multiple: true,
  initialValue?: T[],
  convertSearchToItem?: (query: string) => T
): PaginatedCombobox<T, true>;

export function usePaginatedCombobox<T extends { id: string | number }>(
  fetchFn: FetchFn<T>,
  multiple: false,
  initialValue?: T | null,
  convertSearchToItem?: (query: string) => T
): PaginatedCombobox<T, false>;

export function usePaginatedCombobox<T extends { id: string | number }, Multiple extends boolean>(
  fetchFn: FetchFn<T>,
  multiple: Multiple,
  initialValue?: SelectedValue<T, Multiple>,
  convertSearchToItem?: (query: string) => T
): PaginatedCombobox<T, Multiple> {
  var initialItems: T[] = [];
  if (initialValue) {
    initialItems = Array.isArray(initialValue) ? (initialValue as T[]) : [initialValue as T];
  }
  const [items, setItems] = useState<T[]>(initialItems);
  const [value, setValue] = useState<SelectedValue<T, Multiple>>(
    initialValue ?? ((multiple ? [] : null) as unknown as SelectedValue<T, Multiple>)
  );
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(true);
  const [trigger, setTrigger] = useState(0);

  const onValueChange: OnValueChange<T, Multiple> = useCallback(
    newValue => {
      setValue(newValue as SelectedValue<T, Multiple>);
    },
    [multiple]
  );

  useEffect(() => {
    setValue(initialValue ?? ((multiple ? [] : null) as unknown as SelectedValue<T, Multiple>));

    if (initialValue) {
      const initialItems = Array.isArray(initialValue) ? (initialValue as T[]) : [initialValue as T];
      setItems(prev => {
        const next = [...prev];
        let changed = false;
        initialItems.forEach(item => {
          if (item && !next.find(i => String(i.id) === String(item.id))) {
            next.push(item);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [initialValue, multiple]);

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const valueRef = useRef(value);
  valueRef.current = value;

  const searchQueryRef = useRef<string | undefined>(undefined);
  const lastRequestedPageRef = useRef(0);

  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  useEffect(() => {
    let active = true;
    setIsLoadingMore(true);

    fetchRef
      .current(searchQuery, page)
      .then(result => {
        if (!active) return;
        setItems(prev => {
          const next = page === 0 ? [] : [...prev];
          result.content.forEach(item => {
            const idx = next.findIndex(d => d.id === item.id);
            if (idx === -1) next.push(item);
            else next[idx] = item;
          });

          // Retain selected items in the list so their labels/badges resolve correctly
          const selectedItems = Array.isArray(valueRef.current)
            ? (valueRef.current as T[])
            : valueRef.current
            ? [valueRef.current as T]
            : [];
          selectedItems.forEach(item => {
            if (item && !next.find(i => String(i.id) === String(item.id))) {
              next.push(item);
            }
          });

          return next;
        });

        // Keep the selected value object in sync with freshly fetched rows (e.g. snapshot reload).
        setValue(prev => {
          if (multiple) {
            const selected = (prev as T[]) ?? [];
            if (!selected.length) return prev;
            let changed = false;
            const next = selected.map(item => {
              const fresh = result.content.find(candidate => String(candidate.id) === String(item.id));
              if (fresh) {
                changed = true;
                return fresh;
              }
              return item;
            });
            return (changed ? next : prev) as SelectedValue<T, Multiple>;
          }

          const selected = prev as T | null | undefined;
          if (!selected) return prev;
          const fresh = result.content.find(candidate => String(candidate.id) === String(selected.id));
          return (fresh ?? prev) as SelectedValue<T, Multiple>;
        });

        setHasMore(result.pagination.totalPages > result.pagination.page);
        setIsLoadingMore(false);
      })
      .catch(err => {
        console.error(err);
        if (active) {
          setIsLoadingMore(false);
        }
      });

    return () => {
      active = false;
    };
  }, [searchQuery, page, trigger, multiple]);

  const onLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    if (!hasMore) return;

    const nextPage = page + 1;
    if (nextPage <= lastRequestedPageRef.current) {
      return;
    }
    lastRequestedPageRef.current = nextPage;
    setIsLoadingMore(true);
    setPage(nextPage);
  }, [isLoadingMore, hasMore, page]);

  const reset = useCallback(() => {
    setPage(0);
    setSearchQuery(undefined);
    lastRequestedPageRef.current = 0;
    searchQueryRef.current = undefined;
    setHasMore(true);
    setIsLoadingMore(true);
    setItems([]);
    // Clear selection so a prior value isn't re-appended into the next fetch
    // (e.g. Coach snapshot retained after switching Overview to a Player).
    setValue((multiple ? [] : null) as unknown as SelectedValue<T, Multiple>);
    setTrigger(prev => prev + 1);
  }, [multiple]);

  const reload = useCallback(() => {
    setPage(0);
    setSearchQuery(undefined);
    lastRequestedPageRef.current = 0;
    searchQueryRef.current = undefined;
    setHasMore(true);
    setIsLoadingMore(true);
    setTrigger(prev => prev + 1);
  }, []);

  const onSearch = useCallback((query: string) => {
    const nextQuery = query || undefined;
    if (searchQueryRef.current === nextQuery) {
      return;
    }
    searchQueryRef.current = nextQuery;

    lastRequestedPageRef.current = 0;
    setSearchQuery(nextQuery);
    setPage(0);
    setHasMore(true);
    setIsLoadingMore(true);

    // Immediately clear non-selected items to show a clean loading state
    const selectedItems = Array.isArray(valueRef.current)
      ? (valueRef.current as T[])
      : valueRef.current
      ? [valueRef.current as T]
      : [];
    setItems(selectedItems);
  }, []);

  const allowAdditions = useMemo(() => convertSearchToItem !== undefined, [convertSearchToItem]);
  const onAddItem = useCallback(
    (query: string) => {
      const newItem = convertSearchToItem?.(query);
      if (newItem) {
        setItems(prev => [...prev, newItem]);
      }
      return newItem;
    },
    [convertSearchToItem]
  );

  const isItemEqualToValue = useCallback(
    (item: T, selected: T) => !!item && !!selected && String(item.id) === String(selected.id),
    []
  );

  return useMemo(
    () =>
      ({
        items,
        value,
        hasMore,
        isLoadingMore,
        multiple,
        searchable: true,
        maxChips: "auto" as const,
        allowAdditions,
        onAddItem,
        isItemEqualToValue,
        onValueChange,
        onLoadMore,
        onSearch,
        reset,
        reload,
      } as PaginatedCombobox<T, Multiple>),
    [
      items,
      value,
      hasMore,
      isLoadingMore,
      multiple,
      allowAdditions,
      onAddItem,
      isItemEqualToValue,
      onValueChange,
      onLoadMore,
      onSearch,
      reset,
      reload,
    ]
  );
}
